import { z } from 'zod';
import { objectId, timestampFields, FieldMetadata } from './base';

/**
 * PaymentProduct Schema
 * Represents a payment product linked to Stripe
 */

// Stripe sub-schema
export const StripeInfoSchema = z.object({
  productId: z.string().optional(),
  priceId: z.string().optional(),
  donationPriceId: z.string().optional(),
  accountId: z.string().optional(),
});

export type StripeInfo = z.infer<typeof StripeInfoSchema>;

export const PaymentProductSchema = z.object({
  _id: objectId,
  name: z.string().optional(),
  amount: z.number().optional(),
  description: z.string().optional(),
  active: z.boolean().optional(),
  isFree: z.boolean().optional(),
  isDonation: z.boolean().optional(),
  perTeam: z.boolean().optional(),
  perPlayer: z.boolean().optional(),
  playerPaySeparately: z.boolean().optional(), // When true and perPlayer is true, each player pays individually
  // INT-0018 payment plans: the admin-chosen installment count (2–4).
  // Absent/undefined = plans disabled (today's behavior). Monthly cadence,
  // first installment at checkout; math in utils/installments.ts. NO
  // .default() — absent must mean "disabled", per the config-default rule.
  installments: z.number().optional(),
  // Multi-team discount: a user's Nth team in the same registration pays
  // `additionalTeamAmount` (cents) instead of `amount`. Threshold is the
  // number of *prior paid teams* required to trigger (default 1 = "from
  // the second team on"). Both fields must be set to activate.
  additionalTeamAmount: z.number().optional(),
  additionalTeamThreshold: z.number().default(1),
  // Early-bird pricing: before `earlyBirdDeadline`, the user is charged
  // `earlyBirdAmount` (cents) instead of `amount`. Both fields must be
  // set to activate. After the deadline, the base `amount` applies.
  earlyBirdAmount: z.number().optional(),
  earlyBirdDeadline: z.date().optional(),
  // Late registration: on/after `lateRegistrationStartDate`, the user is charged
  // `lateRegistrationAmount` (cents) instead of `amount` (a late fee, typically
  // higher). Both fields must be set to activate. In practice mutually exclusive
  // with early-bird — the admin sets `earlyBirdDeadline` <= `lateRegistrationStartDate`
  // with the base `amount` applying in between.
  lateRegistrationAmount: z.number().optional(),
  lateRegistrationStartDate: z.date().optional(),
  stripe: StripeInfoSchema.optional(),
  organization: objectId,
  ...timestampFields,
});

export type PaymentProduct = z.infer<typeof PaymentProductSchema>;

export const paymentProductMetadata: Record<string, FieldMetadata> = {
  _id: { hidden: true },
  name: { label: 'Name', editable: true },
  amount: { label: 'Amount', editable: true },
  description: { label: 'Description', editable: true },
  active: { label: 'Active', editable: true },
  isFree: { label: 'Is Free', editable: true },
  isDonation: { label: 'Is Donation', editable: true },
  perTeam: { label: 'Per Team', editable: true },
  perPlayer: { label: 'Per Player', editable: true },
  playerPaySeparately: { label: 'Player Pay Separately', editable: true },
  installments: { label: 'Installments (2–4, blank = off)', editable: true },
  additionalTeamAmount: { label: 'Additional Team Price (cents)', editable: true },
  additionalTeamThreshold: { label: 'Discount kicks in after N teams', editable: true },
  earlyBirdAmount: { label: 'Early-bird Price (cents)', editable: true },
  earlyBirdDeadline: { label: 'Early-bird Deadline', editable: true },
  lateRegistrationAmount: { label: 'Late Registration Price (cents)', editable: true },
  lateRegistrationStartDate: { label: 'Late Registration Start', editable: true },
  stripe: { hidden: true },
  organization: { ref: 'Organization', label: 'Organization' },
  created: { hidden: true },
  updated: { hidden: true },
};
